import { useCallback, useEffect, useRef, useState } from "react";
import { useLoader } from "../../context/LoaderContext";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { getUsrData } from "../../api/loginServiceApi/getUserData";
import { PreworkSummaryModel } from "../../types/PreworksummaryModel";
import { GetPreworkSummaryData } from "../../api/preworkSummaryApi/preworkSummaryApi";
import { Box, Button, IconButton, InputLabel, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { downloadFile } from "../../api/downstreamSummaryApi/downstreamSummaryApi";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { Col, Form, Row } from "react-bootstrap";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import moment from "moment";
import NoteIcon from "@mui/icons-material/Summarize";
import Notes from "../Alert/Notes";
import { useSnackbar } from "../Common/SnackbarContext";
import { urlConfig } from "../../config/urlConfig";
import { appSettings } from "../../config/appSetting";
import { useNavigate } from "react-router-dom";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { FormatDate, FormatDateWithoutTime } from "../Common/CommonMethods";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface ChildComponentProps {
  email: string;
}
const PreworkSummary: React.FC<ChildComponentProps> = ({ email }) => {
  const [showLoader, setShowLoader] = useState(false);
  const { setLoading } = useLoader();
  const { showSnackbar } = useSnackbar();
  const [gridRecordExist, setGridRecordExist] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaultPageSize = appSettings.agGridDefaulPageSize;
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const [tabType, setTabType] = useState<number>(2);
  const [activeButton, setActiveButton] = useState<string>("");
  const [inProcessCount, setInProcessCount] = useState<number>(0); // In-Process tab count
  const [completedCount, setCompletedCount] = useState<number>(0); // Completed tab count
  const [allCount, setAllCount] = useState<number>(0); // All tab count
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesData, setNotesData] = useState<any>(null);
  const [type, setType] = useState<string>("parent");
  const navigate = useNavigate();

  // GetRequired Tokens
  const FetchTokenClaims = async () => {
    try {
      if (email) {
        getUsrData(email.toString());
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // On page load to show data for In-Process tab
  useEffect(() => {
    const init = async () => {
      // Ensure FetchTokenClaims completes before checking localStorage
      await FetchTokenClaims();
      // Check if required localStorage items exist
      if (
        LocalStorageUtil.getItem("userId") &&
        LocalStorageUtil.getItem("contextName") &&
        LocalStorageUtil.getItem("token")
      ) {
        const parameter: PreworkSummaryModel = {
          AcceptedBy: undefined,
          PreWorkDocumentID: undefined,
          DownloadFileName: undefined,
          ProjectDocumentId: undefined,
          MetadataZipName: undefined,
          Fileid: undefined,
          Flename: undefined,
          Project_id: undefined,
          Documents: undefined,
          Documentid: undefined,
          Instructions: undefined,
          Instructionsize: undefined,
          Workarea: undefined,
          Uploadedon: undefined,
          UploadBy: undefined,
          Orderby: undefined,
          AccountId: Number(LocalStorageUtil.getItem("accountId")),
          CreatedBy: undefined,
          Username: undefined,
          CreateDate: undefined,
          Filecount: undefined,
          Rejectremarks: undefined,
          Fileuploadname: undefined,
          Fileuploadsize: undefined,
          RenameFiles: undefined,
          RenameRemarks: undefined,
          DocumentStatusValue: undefined,
          Documentstatus: 2,
          Doucmenttype: undefined,
          Bytesdata: undefined,
          Projectreference: undefined,
          DocumentStream: undefined,
          Acceptby: undefined,
          Statusname: undefined,
          InstructionCount: undefined,
          Acceptedon: undefined,
          FileType: undefined,
          AccepectId: undefined,
          FromDate: null,
          ToDate: null,
          Datebasedocid: 2,
          Databasedcounts: undefined,
          Serialnumber: undefined,
          Acceptedbyuser: undefined,
          Search: undefined,
          DocumentStreamID: undefined,
          Timezone: appSettings.timezone,
          DAOflag: undefined,
          ManagerID: undefined,
          FileSize: undefined,
          DocumentDescription: undefined,
          WorkOrderSubmittedDate: undefined,
          SupplierDueDate: undefined,
          Comments: undefined,
          SupplierTrackingId: undefined,
          AcceptedDatetime: undefined,
          FromPreflightJobNumber: undefined,
          ToPreflightJobNumber: undefined,
          UserId: Number(LocalStorageUtil.getItem("userId")),
          IsSuperAdmin: undefined,
          FolderRights: undefined,
          AccessPermission: undefined,
          DAOReurn: undefined,
          DSAreurn: undefined,
          DeliveryReturn: undefined,
          AgentFirstName: undefined,
          AgentLastName: undefined,
          ContractNumber: undefined,
          Date: undefined,
          Family_Id: undefined,
          DocumentType1: undefined,
          Group_Id: undefined,
          DateOfBirth: undefined,
          Name: undefined,
          Group_Name: undefined,
          GlanName: undefined,
          FundCode: undefined,
          SponsorName: undefined,
          SponsorCode: undefined,
          SubscriberID: undefined,
          MailingDate: undefined,
          MailingCode: undefined,
          LastName: undefined,
          FirstName: undefined,
          PlanYear: undefined,
          ApprovedDate: undefined,
          ApprovedBy: undefined,
          BenefitClassCode: undefined,
          SortColumn: undefined,
          SortOrder: 1,
          CurrentPage: 0,
          NoOfRecords: 0,
          ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        };
        // Fetch data only after the condition is met
        fetchData(parameter);
        // Set the active button to In-Process
        setActiveButton("IN-PROCESS");
      }
    };

    init(); // Call the async function
  }, []); // Dependency array remains empty to run only once

  // Fetch data from API
  const fetchData = async (params: any) => {
    try {
      setShowLoader(true);
      const response = await GetPreworkSummaryData(params);
      if (response !== undefined && response[0].RecordCounts > 0) {
        setGridRecordExist(true);
        setRowData(response);
        setInProcessCount(response[0].InprogressCounts);
        setCompletedCount(response[0].CompletedCounts);
        setAllCount(response[0].AllCounts);

        // Define columns to exclude
        const columnsToExclude = [
          "RowNum",
          "RecordCounts",
          "InprogressCounts",
          "CompletedCounts",
          "AllCounts",
          "DocumentStatus",
          "StatusName",
          "Filetype",
          "documenttype",
          "Instructions",
          "DocumentStreamID",
          "PreWorkDocumentID",
          "DocumentID",
          "DocumentDescription",
          "WorkOrderSubmittedDate",
          "SupplierDueDate",
          "Comments",
          "SupplierTrackingId",
          "AcceptedDatetime",
          "fileSize",
          "DownloadFileName",
          "FromPreflightJobNumber",
          "ToPreflightJobNumber",
          "ManagerID",
        ];

        // Dynamically get column headers from the data
        const dynamicColumns = Object.keys(response[0])
          .filter((key) => !columnsToExclude.includes(key))
          .map((key: string) => {
            if (
              key.toLocaleLowerCase().includes("uploadedon") ||
              key.toLocaleLowerCase().includes("acceptedon") ||
              key.toLocaleLowerCase().includes("date")
            ) {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                valueFormatter: (params: any) =>
                  key.toLocaleLowerCase() === "supplement date" ||
                  key.toLocaleLowerCase() === "revised date" ||
                  key.toLocaleLowerCase() === "amendment date"
                    ? FormatDateWithoutTime(params.value)
                    : FormatDate(params.value),
              };
            } else if (key.toLocaleLowerCase() === "instructioncount") {
              const renderCell = (params: any) => {
                console.log("params", params.value);
                return params.value > 0 ? (
                  <Tooltip title="Click to view the notes">
                    <IconButton
                      style={{
                        color: "var(--aggrid-text-color)",
                        justifyContent: "left",
                      }}
                      onClick={() => viewNotes(params)}
                    >
                      <NoteIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  "NA"
                );
              };
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                width: 120,
                cellRenderer: renderCell,
              };
            } else {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                width: 200,
              };
            }
          });

        // Combine static and dynamic column definitions
        const combinedColumnDefs = [
          {
            headerName: "#",
            width: 70,
            suppressSizeToFit: true,
            colId: "colRowNum",
            sortable: false,
            filter: false,
            resizable: false,
            enableRowGroup: false,
            valueGetter: (params: any) => {
              return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
            },
          },
          ...dynamicColumns,
          {
            headerName: "Status name",
            field: "StatusName",
            sortable: true,
            filter: true,
            enableRowGroup: true,
            hide: params.Documentstatus === 2 || params.Documentstatus === 3,
          },
          {
            field: "Actions",
            headerName: "Actions",
            sortable: false,
            enableRowGroup: false,
            pinned: "right" as const,
            lockPinned: true,
            suppressSizeToFit: true,
            width: 100,
            minWidth: 100,
            cellClass: "actions-button-cell",
            cellRenderer: actionCellRenderer,
            filter: false,
            resizable: false,
            colId: "colAction",
          },
        ];

        setColumnDefs(combinedColumnDefs);
      } else {
        setInProcessCount(response[0].InProgress);
        setCompletedCount(response[0].Completed);
        setAllCount(response[0].AllCount);
        setColumnDefs([]);
        setRowData([]);
        setGridRecordExist(false);
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  // Helper function to get column name mapping
  const getColumnNameMapping = (): Record<string, string> => {
    return {
      filename: "File Name",
      uploadedon: "Uploaded On",
      uploadby: "Uploaded By",
      acceptedon: "Accepted On",
      acceptedby: "Accepted By",
      instructioncount: "Notes",
    };
  };

  // Rename the columns
  const renameColumns = (val: any): string | undefined => {
    if (val === undefined) {
      return undefined;
    }

    const lowerVal = val.toLowerCase();
    const columnMapping = getColumnNameMapping();

    // Check if there's a direct mapping
    if (columnMapping[lowerVal]) {
      return columnMapping[lowerVal];
    }

    // Default behavior: replace &amp; with &
    return val.replace(/&amp;/, "&");
  };

  // View Document - Action buttons events
  const viewDocument = (rowData: any) => {
    const id = rowData.DocumentStreamID;
    const encodedId = btoa(id);
    const fileName = rowData.DownloadFileName.replace(/\.[^/.]+$/, "");
    const url = !urlConfig.isDeployed
      ? `/documentviewer/${encodedId}?FileName=${fileName}`
      : `/docdistrov2/documentviewer/${encodedId}?FileName=${fileName}`;
    popupCenter(url, "Document Viewer", 1100, 550);
  };

  // File Download - Action buttons events
  const fileDownload = async (params: any) => {
    const objDownloadFile = {
      documentStreamID: params.DocumentStreamID,
      contextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    try {
      setLoading(true);
      const response = await downloadFile(objDownloadFile);
      if (response.status === 200) {
        setLoading(false);
        const data = response.data;
        const contentType = response.headers["content-type"];
        const linkElement = document.createElement("a");
        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        linkElement.href = url;
        linkElement.setAttribute("download", params.DownloadFileName);
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: false,
        });
        linkElement.dispatchEvent(clickEvent);
      }
      if (response) {
        showSnackbar("Documents downloaded successfully");
      } else {
        showSnackbar("Unable to download file !");
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Donwload file pop up
  const popupCenter = (url: string, title: string, w: number, h: number) => {
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      window.screen.width;

    const height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      window.screen.height;

    const left = width / 3 - w / 3 + dualScreenLeft;
    const top = height / 3 - h / 3 + dualScreenTop;
    const newWindow = window.open(
      url,
      title,
      `readonly=true, scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`
    );
    if (newWindow) {
      newWindow.focus();
    }
  };

  // Action cell renderer
  const actionCellRenderer = (params: any) => {
    console.log("params data", params.data);
    const rowData = params.data;
    if (params.data === undefined) {
      return "";
    } else {
      return (
        <div>
          {isPagePermission("Allow Download", "PreworkSummary", 105) && (
            <Tooltip title="Click here to download the documents">
              <IconButton
                style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
                onClick={() => fileDownload(rowData)}
              >
                <FileDownloadIcon className="icon-button-other" />
              </IconButton>
            </Tooltip>
          )}
          {isPagePermission("Allow DocumentPreView", "PreworkSummary", 109) && (
            <Tooltip title="Click to view">
              <IconButton
                style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
                onClick={() => viewDocument(rowData)}
              >
                <VisibilityIcon className="icon-button-other" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      );
    }
  };

  // Aandel In-Process tab click event
  const handleInProcessTabClick = (tabType: number, buttonName: string) => {
    setActiveButton(buttonName);
    setTabType(tabType);
    const parameter: PreworkSummaryModel = {
      AcceptedBy: undefined,
      PreWorkDocumentID: undefined,
      DownloadFileName: undefined,
      ProjectDocumentId: undefined,
      MetadataZipName: undefined,
      Fileid: undefined,
      Flename: undefined,
      Project_id: undefined,
      Documents: undefined,
      Documentid: undefined,
      Instructions: undefined,
      Instructionsize: undefined,
      Workarea: undefined,
      Uploadedon: undefined,
      UploadBy: undefined,
      Orderby: undefined,
      AccountId: Number(LocalStorageUtil.getItem("accountId")),
      CreatedBy: undefined,
      Username: undefined,
      CreateDate: undefined,
      Filecount: undefined,
      Rejectremarks: undefined,
      Fileuploadname: undefined,
      Fileuploadsize: undefined,
      RenameFiles: undefined,
      RenameRemarks: undefined,
      DocumentStatusValue: undefined,
      Documentstatus: tabType,
      Doucmenttype: undefined,
      Bytesdata: undefined,
      Projectreference: undefined,
      DocumentStream: undefined,
      Acceptby: undefined,
      Statusname: undefined,
      InstructionCount: undefined,
      Acceptedon: undefined,
      FileType: undefined,
      AccepectId: undefined,
      FromDate: null,
      ToDate: null,
      Datebasedocid: 2,
      Databasedcounts: undefined,
      Serialnumber: undefined,
      Acceptedbyuser: undefined,
      Search: undefined,
      DocumentStreamID: undefined,
      Timezone: appSettings.timezone,
      DAOflag: undefined,
      ManagerID: undefined,
      FileSize: undefined,
      DocumentDescription: undefined,
      WorkOrderSubmittedDate: undefined,
      SupplierDueDate: undefined,
      Comments: undefined,
      SupplierTrackingId: undefined,
      AcceptedDatetime: undefined,
      FromPreflightJobNumber: undefined,
      ToPreflightJobNumber: undefined,
      UserId: Number(LocalStorageUtil.getItem("userId")),
      IsSuperAdmin: undefined,
      FolderRights: undefined,
      AccessPermission: undefined,
      DAOReurn: undefined,
      DSAreurn: undefined,
      DeliveryReturn: undefined,
      AgentFirstName: undefined,
      AgentLastName: undefined,
      ContractNumber: undefined,
      Date: undefined,
      Family_Id: undefined,
      DocumentType1: undefined,
      Group_Id: undefined,
      DateOfBirth: undefined,
      Name: undefined,
      Group_Name: undefined,
      GlanName: undefined,
      FundCode: undefined,
      SponsorName: undefined,
      SponsorCode: undefined,
      SubscriberID: undefined,
      MailingDate: undefined,
      MailingCode: undefined,
      LastName: undefined,
      FirstName: undefined,
      PlanYear: undefined,
      ApprovedDate: undefined,
      ApprovedBy: undefined,
      BenefitClassCode: undefined,
      SortColumn: undefined,
      SortOrder: 1,
      CurrentPage: 0,
      NoOfRecords: 0,
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    fetchData(parameter);
  };

  // handel Completed tab click event
  const handleCompletedTabClick = (tabType: number, buttonName: string) => {
    setActiveButton(buttonName);
    setTabType(tabType);
    setStartDate(null);
    setEndDate(null);
    const parameter: PreworkSummaryModel = {
      AcceptedBy: undefined,
      PreWorkDocumentID: undefined,
      DownloadFileName: undefined,
      ProjectDocumentId: undefined,
      MetadataZipName: undefined,
      Fileid: undefined,
      Flename: undefined,
      Project_id: undefined,
      Documents: undefined,
      Documentid: undefined,
      Instructions: undefined,
      Instructionsize: undefined,
      Workarea: undefined,
      Uploadedon: undefined,
      UploadBy: undefined,
      Orderby: undefined,
      AccountId: Number(LocalStorageUtil.getItem("accountId")),
      CreatedBy: undefined,
      Username: undefined,
      CreateDate: undefined,
      Filecount: undefined,
      Rejectremarks: undefined,
      Fileuploadname: undefined,
      Fileuploadsize: undefined,
      RenameFiles: undefined,
      RenameRemarks: undefined,
      DocumentStatusValue: undefined,
      Documentstatus: tabType,
      Doucmenttype: undefined,
      Bytesdata: undefined,
      Projectreference: undefined,
      DocumentStream: undefined,
      Acceptby: undefined,
      Statusname: undefined,
      InstructionCount: undefined,
      Acceptedon: undefined,
      FileType: undefined,
      AccepectId: undefined,
      FromDate: null,
      ToDate: null,
      Datebasedocid: 2,
      Databasedcounts: undefined,
      Serialnumber: undefined,
      Acceptedbyuser: undefined,
      Search: undefined,
      DocumentStreamID: undefined,
      Timezone: appSettings.timezone,
      DAOflag: undefined,
      ManagerID: undefined,
      FileSize: undefined,
      DocumentDescription: undefined,
      WorkOrderSubmittedDate: undefined,
      SupplierDueDate: undefined,
      Comments: undefined,
      SupplierTrackingId: undefined,
      AcceptedDatetime: undefined,
      FromPreflightJobNumber: undefined,
      ToPreflightJobNumber: undefined,
      UserId: Number(LocalStorageUtil.getItem("userId")),
      IsSuperAdmin: undefined,
      FolderRights: undefined,
      AccessPermission: undefined,
      DAOReurn: undefined,
      DSAreurn: undefined,
      DeliveryReturn: undefined,
      AgentFirstName: undefined,
      AgentLastName: undefined,
      ContractNumber: undefined,
      Date: undefined,
      Family_Id: undefined,
      DocumentType1: undefined,
      Group_Id: undefined,
      DateOfBirth: undefined,
      Name: undefined,
      Group_Name: undefined,
      GlanName: undefined,
      FundCode: undefined,
      SponsorName: undefined,
      SponsorCode: undefined,
      SubscriberID: undefined,
      MailingDate: undefined,
      MailingCode: undefined,
      LastName: undefined,
      FirstName: undefined,
      PlanYear: undefined,
      ApprovedDate: undefined,
      ApprovedBy: undefined,
      BenefitClassCode: undefined,
      SortColumn: undefined,
      SortOrder: 1,
      CurrentPage: 0,
      NoOfRecords: 0,
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    fetchData(parameter);
  };

  // handel All tab click event
  const handleAllTabClick = (tabType: number, buttonName: string) => {
    setActiveButton(buttonName);
    setTabType(tabType);
    const parameter: PreworkSummaryModel = {
      AcceptedBy: undefined,
      PreWorkDocumentID: undefined,
      DownloadFileName: undefined,
      ProjectDocumentId: undefined,
      MetadataZipName: undefined,
      Fileid: undefined,
      Flename: undefined,
      Project_id: undefined,
      Documents: undefined,
      Documentid: undefined,
      Instructions: undefined,
      Instructionsize: undefined,
      Workarea: undefined,
      Uploadedon: undefined,
      UploadBy: undefined,
      Orderby: undefined,
      AccountId: Number(LocalStorageUtil.getItem("accountId")),
      CreatedBy: undefined,
      Username: undefined,
      CreateDate: undefined,
      Filecount: undefined,
      Rejectremarks: undefined,
      Fileuploadname: undefined,
      Fileuploadsize: undefined,
      RenameFiles: undefined,
      RenameRemarks: undefined,
      DocumentStatusValue: undefined,
      Documentstatus: tabType,
      Doucmenttype: undefined,
      Bytesdata: undefined,
      Projectreference: undefined,
      DocumentStream: undefined,
      Acceptby: undefined,
      Statusname: undefined,
      InstructionCount: undefined,
      Acceptedon: undefined,
      FileType: undefined,
      AccepectId: undefined,
      FromDate: null,
      ToDate: null,
      Datebasedocid: 2,
      Databasedcounts: undefined,
      Serialnumber: undefined,
      Acceptedbyuser: undefined,
      Search: undefined,
      DocumentStreamID: undefined,
      Timezone: appSettings.timezone,
      DAOflag: undefined,
      ManagerID: undefined,
      FileSize: undefined,
      DocumentDescription: undefined,
      WorkOrderSubmittedDate: undefined,
      SupplierDueDate: undefined,
      Comments: undefined,
      SupplierTrackingId: undefined,
      AcceptedDatetime: undefined,
      FromPreflightJobNumber: undefined,
      ToPreflightJobNumber: undefined,
      UserId: Number(LocalStorageUtil.getItem("userId")),
      IsSuperAdmin: undefined,
      FolderRights: undefined,
      AccessPermission: undefined,
      DAOReurn: undefined,
      DSAreurn: undefined,
      DeliveryReturn: undefined,
      AgentFirstName: undefined,
      AgentLastName: undefined,
      ContractNumber: undefined,
      Date: undefined,
      Family_Id: undefined,
      DocumentType1: undefined,
      Group_Id: undefined,
      DateOfBirth: undefined,
      Name: undefined,
      Group_Name: undefined,
      GlanName: undefined,
      FundCode: undefined,
      SponsorName: undefined,
      SponsorCode: undefined,
      SubscriberID: undefined,
      MailingDate: undefined,
      MailingCode: undefined,
      LastName: undefined,
      FirstName: undefined,
      PlanYear: undefined,
      ApprovedDate: undefined,
      ApprovedBy: undefined,
      BenefitClassCode: undefined,
      SortColumn: undefined,
      SortOrder: 1,
      CurrentPage: 0,
      NoOfRecords: 0,
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    fetchData(parameter);
  };

  // Handle Export to excel
  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "PreworkSummaryReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "PreworkSummary Report",
        columnKeys: columnDefs
          .map((colDef) => colDef.field)
          .filter((field): field is string => typeof field === "string"),
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  // Apply button click event
  const handleApplyButton = () => {
    if (startDate === null) {
      showSnackbar("From date cannot be empty", true);
    } else if (endDate === null) {
      showSnackbar("To date cannot be empty", true);
    } else if (startDate !== null && endDate !== null && startDate > endDate) {
      showSnackbar("From date should be less than To date", true);
    } else {
      const parameter: PreworkSummaryModel = {
        AcceptedBy: undefined,
        PreWorkDocumentID: undefined,
        DownloadFileName: undefined,
        ProjectDocumentId: undefined,
        MetadataZipName: undefined,
        Fileid: undefined,
        Flename: undefined,
        Project_id: undefined,
        Documents: undefined,
        Documentid: undefined,
        Instructions: undefined,
        Instructionsize: undefined,
        Workarea: undefined,
        Uploadedon: undefined,
        UploadBy: undefined,
        Orderby: undefined,
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        CreatedBy: undefined,
        Username: undefined,
        CreateDate: undefined,
        Filecount: undefined,
        Rejectremarks: undefined,
        Fileuploadname: undefined,
        Fileuploadsize: undefined,
        RenameFiles: undefined,
        RenameRemarks: undefined,
        DocumentStatusValue: undefined,
        Documentstatus: tabType,
        Doucmenttype: undefined,
        Bytesdata: undefined,
        Projectreference: undefined,
        DocumentStream: undefined,
        Acceptby: undefined,
        Statusname: undefined,
        InstructionCount: undefined,
        Acceptedon: undefined,
        FileType: undefined,
        AccepectId: undefined,
        FromDate: startDate,
        ToDate: endDate,
        Datebasedocid: 2,
        Databasedcounts: undefined,
        Serialnumber: undefined,
        Acceptedbyuser: undefined,
        Search: undefined,
        DocumentStreamID: undefined,
        Timezone: appSettings.timezone,
        DAOflag: undefined,
        ManagerID: undefined,
        FileSize: undefined,
        DocumentDescription: undefined,
        WorkOrderSubmittedDate: undefined,
        SupplierDueDate: undefined,
        Comments: undefined,
        SupplierTrackingId: undefined,
        AcceptedDatetime: undefined,
        FromPreflightJobNumber: undefined,
        ToPreflightJobNumber: undefined,
        UserId: Number(LocalStorageUtil.getItem("userId")),
        IsSuperAdmin: undefined,
        FolderRights: undefined,
        AccessPermission: undefined,
        DAOReurn: undefined,
        DSAreurn: undefined,
        DeliveryReturn: undefined,
        AgentFirstName: undefined,
        AgentLastName: undefined,
        ContractNumber: undefined,
        Date: undefined,
        Family_Id: undefined,
        DocumentType1: undefined,
        Group_Id: undefined,
        DateOfBirth: undefined,
        Name: undefined,
        Group_Name: undefined,
        GlanName: undefined,
        FundCode: undefined,
        SponsorName: undefined,
        SponsorCode: undefined,
        SubscriberID: undefined,
        MailingDate: undefined,
        MailingCode: undefined,
        LastName: undefined,
        FirstName: undefined,
        PlanYear: undefined,
        ApprovedDate: undefined,
        ApprovedBy: undefined,
        BenefitClassCode: undefined,
        SortColumn: undefined,
        SortOrder: 1,
        CurrentPage: 0,
        NoOfRecords: 0,
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
      };
      fetchData(parameter);
    }
  };

  //handel reset button
  const handleReset = () => {
    const parameter: PreworkSummaryModel = {
      AcceptedBy: undefined,
      PreWorkDocumentID: undefined,
      DownloadFileName: undefined,
      ProjectDocumentId: undefined,
      MetadataZipName: undefined,
      Fileid: undefined,
      Flename: undefined,
      Project_id: undefined,
      Documents: undefined,
      Documentid: undefined,
      Instructions: undefined,
      Instructionsize: undefined,
      Workarea: undefined,
      Uploadedon: undefined,
      UploadBy: undefined,
      Orderby: undefined,
      AccountId: Number(LocalStorageUtil.getItem("accountId")),
      CreatedBy: undefined,
      Username: undefined,
      CreateDate: undefined,
      Filecount: undefined,
      Rejectremarks: undefined,
      Fileuploadname: undefined,
      Fileuploadsize: undefined,
      RenameFiles: undefined,
      RenameRemarks: undefined,
      DocumentStatusValue: undefined,
      Documentstatus: tabType,
      Doucmenttype: undefined,
      Bytesdata: undefined,
      Projectreference: undefined,
      DocumentStream: undefined,
      Acceptby: undefined,
      Statusname: undefined,
      InstructionCount: undefined,
      Acceptedon: undefined,
      FileType: undefined,
      AccepectId: undefined,
      FromDate: null,
      ToDate: null,
      Datebasedocid: 2,
      Databasedcounts: undefined,
      Serialnumber: undefined,
      Acceptedbyuser: undefined,
      Search: undefined,
      DocumentStreamID: undefined,
      Timezone: appSettings.timezone,
      DAOflag: undefined,
      ManagerID: undefined,
      FileSize: undefined,
      DocumentDescription: undefined,
      WorkOrderSubmittedDate: undefined,
      SupplierDueDate: undefined,
      Comments: undefined,
      SupplierTrackingId: undefined,
      AcceptedDatetime: undefined,
      FromPreflightJobNumber: undefined,
      ToPreflightJobNumber: undefined,
      UserId: Number(LocalStorageUtil.getItem("userId")),
      IsSuperAdmin: undefined,
      FolderRights: undefined,
      AccessPermission: undefined,
      DAOReurn: undefined,
      DSAreurn: undefined,
      DeliveryReturn: undefined,
      AgentFirstName: undefined,
      AgentLastName: undefined,
      ContractNumber: undefined,
      Date: undefined,
      Family_Id: undefined,
      DocumentType1: undefined,
      Group_Id: undefined,
      DateOfBirth: undefined,
      Name: undefined,
      Group_Name: undefined,
      GlanName: undefined,
      FundCode: undefined,
      SponsorName: undefined,
      SponsorCode: undefined,
      SubscriberID: undefined,
      MailingDate: undefined,
      MailingCode: undefined,
      LastName: undefined,
      FirstName: undefined,
      PlanYear: undefined,
      ApprovedDate: undefined,
      ApprovedBy: undefined,
      BenefitClassCode: undefined,
      SortColumn: undefined,
      SortOrder: 1,
      CurrentPage: 0,
      NoOfRecords: 0,
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };

    // Fetch data only after the condition is met
    fetchData(parameter);
    // Set the active button to In-Process
    setActiveButton("IN-PROCESS");
    setStartDate(null);
    setEndDate(null);
  };

  //#region QuickFilter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);
  //#end filter

  // Close notes popup
  const closeNotesPopup = () => {
    setNotesOpen(false);
    setNotesData(null);
  };

  const viewNotes = (rowData: any) => {
    if (gridRef.current) {
      setNotesOpen(true);
      setNotesData(rowData.data);
      setType("Parent");
    }
  };

  //#region Upload UI Screen
  const handleUploadClick = () => {
    navigate("/fileUpload", { state: { screen: "preworksummary" } });
  };
  //#endregion

  return (
    <div>
      <NavigationBar title="Prework Summary" text="Prework Summary" />
      <Row style={{ margin: "10px" }}>
        <Col>
          <Box>
            <div className="rp-main-inner col-sm-12">
              <div className="queue-top row clearfix">
                {/* Buttons */}
                <div
                  className="col-sm-8"
                  style={{
                    display: "flex",
                    gap: "5px",
                    alignItems: "center",
                    marginTop: "20px",
                    width: "66%",
                  }}
                >
                  {isPagePermission(
                    "Allow Menu-InProgress",
                    "PreworkSummary",
                    106
                  ) && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleInProcessTabClick(2, "IN-PROCESS")}
                      style={{
                        backgroundColor:
                          activeButton === "IN-PROCESS" ? "#ddd" : "#4D7BA7",
                        color: activeButton === "IN-PROCESS" ? "black" : "#ddd",
                        marginRight: "1em",
                        fontSize: "11px",
                        borderRadius: "4px",
                        border: "1px solid #858c94",
                        height: "25px",
                      }}
                    >
                      IN-PROCESS({inProcessCount})
                    </Button>
                  )}
                  {isPagePermission(
                    "Allow Menu-Completed",
                    "PreworkSummary",
                    107
                  ) && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleCompletedTabClick(3, "COMPLETED")}
                      style={{
                        backgroundColor:
                          activeButton === "COMPLETED" ? "#ddd" : "#4D7BA7",
                        color: activeButton === "COMPLETED" ? "black" : "#ddd",
                        marginRight: "1em",
                        fontSize: "11px",
                        borderRadius: "4px",
                        border: "1px solid #858c94",
                        height: "25px",
                      }}
                    >
                      COMPLETED({completedCount})
                    </Button>
                  )}
                  {isPagePermission(
                    "Allow Menu-All",
                    "PreworkSummary",
                    108
                  ) && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAllTabClick(0, "ALL")}
                      style={{
                        backgroundColor:
                          activeButton === "ALL" ? "#ddd" : "#4D7BA7",
                        color: activeButton === "ALL" ? "black" : "#ddd",
                        marginRight: "1em",
                        fontSize: "11px",
                        borderRadius: "4px",
                        border: "1px solid #858c94",
                        height: "25px",
                      }}
                    >
                      ALL({allCount})
                    </Button>
                  )}
                </div>
                <div
                  className="col-sm-5"
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "right",
                    marginTop: "1em",
                    width: "34%",
                  }}
                >
                  <input
                    type="text"
                    id="filter-text-box"
                    placeholder="Filter..."
                    style={{
                      width: "20em",
                      height: "2.5em",
                      fontSize: "11px",
                      paddingLeft: ".5em",
                      marginBottom: ".1em",
                      marginLeft: "6em",
                      marginRight: "1em",
                    }}
                    onInput={onFilterTextBoxChanged}
                  />
                  {Number(LocalStorageUtil.getItem("isSuperAdmin")) === 1 &&
                  isPagePermission("Allow Upload", "PreworkSummary", 102) ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUploadClick}
                      style={{
                        backgroundColor: "var(--button-background-color)",
                        color: "var(--button-color)",
                        textTransform: "none",
                        fontSize: "11px",
                        paddingTop: "1px",
                        paddingBottom: "5px",
                        paddingLeft: "10px",
                        marginBottom: "1em",
                        whiteSpace: "normal",
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px",
                        borderBottomLeftRadius: "0px",
                        borderBottomRightRadius: "0px",
                        width: "1em",
                      }}
                    >
                      Upload
                    </Button>
                  ) : null}
                </div>

                <div
                  className="col-sm-12"
                  style={{
                    display: "flex",
                    gap: "5px",
                    alignItems: "center",
                    marginTop: "20px",
                  }}
                >
                  {/* Dates, Apply and Reset buttons */}
                  <div
                    className="col-sm-8"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      From Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="From Date"
                      name="fromdate"
                      max={new Date().toISOString().split("T")[0]}
                      value={
                        startDate ? startDate.toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => setStartDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                        marginTop: ".1em",
                      }}
                    />
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      To Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="To Date"
                      name="todate"
                      value={endDate ? endDate.toISOString().split("T")[0] : ""}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                        marginTop: ".1em",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyButton}
                      className="button"
                      style={{
                        marginBottom: ".5em",
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleReset}
                      className="button"
                      style={{
                        marginBottom: ".5em",
                      }}
                    >
                      Reset
                    </Button>
                  </div>

                  {/* Grid Preferences and export to excel */}
                  <div
                    className="col-sm-4"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      content: "flex-end",
                      justifyContent: "flex-end",
                      width: "33%",
                    }}
                  >
                    {gridRecordExist && (
                      <>
                        {isPagePermission(
                          "Allow Export To Excel",
                          "PreworkSummary",
                          104
                        ) && (
                          <Tooltip title="Click to export the grid content in excel">
                            <button
                              type="button"
                              onClick={handleExport}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                              }}
                              aria-label="Export to Excel"
                            >
                              <img
                                src={ExcelIcon}
                                alt="Doc-Distro"
                                style={{ cursor: "pointer", width: "30px" }}
                              />
                            </button>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* AgGrid */}
                <div className="queue-table-view clearfix">
                  <div
                    className="ag-theme-quartz-dark"
                    style={{
                      height: "56vh",
                      width: "100%",
                      marginTop: "1em",
                    }}
                  >
                    <AgGridReact
                      columnDefs={columnDefs}
                      rowData={rowData}
                      ref={gridRef}
                      pagination={true}
                      paginationPageSize={defaultPageSize}
                      paginationPageSizeSelector={pageSizeSelector}
                      headerHeight={30}
                      rowGroupPanelShow="always"
                      pivotPanelShow="always"
                      gridOptions={{
                        rowSelection: {
                          mode: "multiRow",
                          enableClickSelection: false,
                        },
                      }}
                      loading={!rowData || showLoader}
                      loadingOverlayComponent={CustomLoadingOverlay}
                      overlayNoRowsTemplate={
                        '<span class="ag-overlay-loading-center">No rows to show</span>'
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </Box>
        </Col>
      </Row>

      {/* Notes popup */}
      {notesOpen && (
        <Notes
          isOpen={notesOpen}
          onClose={closeNotesPopup}
          title="View Instruction"
          selectedData={notesData}
          gridType={type}
          screen="PreworkSummary"
        />
      )}
    </div>
  );
};
export default PreworkSummary;
